import NiceModal from "@ebay/nice-modal-react";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import cx from "classnames";
import { Button, TranslateWrapper } from "components";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { Icon } from "components/Icon/Icon";
import { useTranslate } from "hooks";
import type {
  AnyFolder,
  AnyProduct,
  Node as NodeType,
} from "interfaces/schemas";
import React, { memo, useCallback, useMemo } from "react";
import { UncontrolledTooltip } from "reactstrap";
import { useCopiedItem } from "stores/project";
import {
  useAddedProducts,
  useProject,
} from "stores/project/project/projectStore";
import * as identify from "utils/treeUtils";
import type { defaultOptions } from "variables/nodeDefaultOptions";
import { disableDrop } from "utils";
import s from "./Styles.module.scss";
import { WizardIndicator } from "./WizardIndicator";

type ChildProps = {
  node: NodeApi<AnyProduct | AnyFolder>;
};

type NodeLayoutProps = {
  node: NodeApi<NodeType>;
  children: React.ReactElement<ChildProps>;
  options: typeof defaultOptions;
  hasChildren: boolean;
  isOpen: boolean;
  onClick: () => void;
  isAncestorDragging: boolean;
  isDragging: boolean;
  willReceiveDrop: boolean;
  onDoubleClick: () => void;
  isPasteAllowed: boolean | null;
  dragNode: NodeApi<NodeType> | null;
  isActiveProductNode: boolean;
};

export const NodeLayout = memo(
  ({
    node,
    children,
    options,
    hasChildren,
    isOpen,
    onClick,
    isAncestorDragging,
    isDragging,
    willReceiveDrop,
    onDoubleClick,
    isPasteAllowed,
    dragNode,
    isActiveProductNode,
  }: NodeLayoutProps) => {
    const translate = useTranslate();
    const project = useProject();
    const copiedItem = useCopiedItem();
    const addedProducts = useAddedProducts();
    const { data } = node;
    const isWizard = identify.isWizard(data);
    const isFolder = identify.isFolder(data);
    const isRoot = identify.isRoot(data);
    const isAnyProduct = identify.isAnyProduct(data);
    const isAnyFolder = identify.isAnyFolder(data);
    const isTroxNetcom = identify.isTroxNetcom(data);
    const isAccessoryProduct = identify.isProductAccessory(data);
    const itemToCompare = copiedItem || dragNode;

    const baseBlurCheck =
      isAncestorDragging ||
      (itemToCompare &&
        disableDrop({
          parentNode: node,
          dragNodes: [itemToCompare],
        }));

    const isBlurred = baseBlurCheck && !addedProducts.includes(data.id);

    const isActionAllowed = (!isBlurred && copiedItem) || !copiedItem;

    const dataTestID = useMemo(() => {
      const nodeProps = children.props.node.data;

      if (identify.isAnyProduct(nodeProps)) {
        return `product-${nodeProps.presentation}`;
      }
      if (identify.isWizard(nodeProps)) {
        return `wizard-${nodeProps.folderName?.replace(/ /g, "")}`;
      }
      if (identify.isFolder(nodeProps)) {
        return `folder-${nodeProps.folderName?.replace(/ /g, "")}`;
      }
      return "";
    }, [children.props.node.data]);

    const openDownloadModal = useCallback(() => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: project?.title || "",
        projectId: project?.id || "",
      });
    }, [project?.id, project?.title]);

    return (
      <div
        className="w-100 h-100 py-2 position-relative text-gray-700"
        style={{ minWidth: "180px" }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        data-testid={`ProjectTree_${dataTestID}`}
      >
        <div
          className={cx(
            "w-100 d-flex flex-row justify-content-between align-items-center rounded-2 border h-100",
            {
              [s.folder]: isAnyFolder,
              [s.configuration]: isAnyProduct,
              "border-info":
                willReceiveDrop && !isDragging && !isAncestorDragging,
              [s.disabledFolder]: isAnyFolder && isBlurred,
              "opacity-50": !isAnyFolder && isBlurred,
              "opacity-25": isDragging,
              [s.pastePreview]: !isBlurred && copiedItem && isPasteAllowed,
              "cursor-pointer": isActionAllowed,
              "cursor-not-allowed": !isActionAllowed,
              "border-info bg-blue-50": isActiveProductNode,
            },
          )}
        >
          <LeftSideComponents
            isWizard={isWizard}
            isTroxNetcom={isTroxNetcom}
            isProduct={identify.isProduct(data)}
            isCustomProduct={identify.isCustomProduct(data)}
            isAnyProduct={isAnyProduct}
            isAccessoryProduct={isAccessoryProduct}
            isFolder={isFolder}
            isRootNode={isRoot}
            options={options}
          />
          <div
            className={cx(
              "d-flex flex-row justify-content-between align-items-center pe-4 w-100 h-100 overflow-hidden",
              {
                "ps-1": isRoot || (options.dndDisabled && !isWizard),
              },
            )}
          >
            {children}

            {isRoot && !isWizard && isActionAllowed && options?.isFullpage && (
              <>
                <Button
                  data-testid="NodeLayout_download-btn"
                  color="transparent"
                  className="px-2 border-0"
                  onClick={openDownloadModal}
                  id="NodeLayout_project_item_download_icon"
                >
                  <Icon
                    iconName="arrow-down-bracket-light"
                    className={cx("icon-16", s.iconButton)}
                  />
                </Button>
                <UncontrolledTooltip
                  target="NodeLayout_project_item_download_icon"
                  placement="top"
                >
                  {translate("DOWNLOAD")}
                </UncontrolledTooltip>
                {typeof translate("DOWNLOAD") === "object" && (
                  <TranslateWrapper
                    tooltipText="DOWNLOAD"
                    tooltipId="NodeLayout_project_item_download_icon"
                  />
                )}
              </>
            )}
          </div>
        </div>
        <BottomSpacer
          options={options}
          hasChildren={hasChildren}
          isOpen={isOpen}
        />
      </div>
    );
  },
);

type LeftSideComponentsProps = {
  isWizard: boolean;
  isTroxNetcom: boolean;
  isAnyProduct: boolean;
  isRootNode: boolean;
  isFolder: boolean;
  isProduct: boolean;
  isCustomProduct: boolean;
  isAccessoryProduct: boolean;
  options: typeof defaultOptions;
};

const LeftSideComponents = memo(
  ({
    isWizard,
    isTroxNetcom,
    isAnyProduct,
    isRootNode,
    isFolder,
    isProduct,
    isCustomProduct,
    isAccessoryProduct,
    options,
  }: LeftSideComponentsProps) => (
    <>
      {(isWizard || isTroxNetcom) && (
        <div
          className={cx("h-100", {
            "pe-2": isAnyProduct,
          })}
        >
          <WizardIndicator />
        </div>
      )}
      {isAccessoryProduct && <div className="pe-3" />}
      {!isRootNode &&
        (isProduct || isFolder || isCustomProduct) &&
        !options.dndDisabled && (
          <div
            className={cx("", {
              "ps-1": !isAnyProduct,
              "ps-3 pe-2": isAnyProduct,
            })}
          >
            <Icon
              iconName="grip-dots-vertical"
              className={cx("icon-12 cursor-pointer", {
                "text-gray-400": isAnyProduct,
                "text-gray-700": !isAnyProduct,
              })}
            />
          </div>
        )}
    </>
  ),
);

type BottomSpacerProps = {
  options: typeof defaultOptions;
  hasChildren: boolean;
  isOpen: boolean;
};

export const BottomSpacer = memo(
  ({ options, hasChildren, isOpen }: BottomSpacerProps) => (
    <>
      {options.isIndentCompact ? (
        <CompactSpacer
          width={options.indentSize}
          hasChildren={hasChildren}
          isOpen={isOpen}
        />
      ) : (
        <Spacer
          width={options.indentSize}
          hasChildren={hasChildren}
          isOpen={isOpen}
        />
      )}
    </>
  ),
);

type SpacerProps = { width: number; hasChildren: boolean; isOpen: boolean };

const Spacer = ({ width, hasChildren, isOpen }: SpacerProps) => (
  <div className={cx("w-100 d-flex flex-row", s.bottomSpacer)}>
    <div className={cx("h-100 d-flex flex-row")} style={{ width }}>
      <div className="w-50 h-100"></div>
      <div
        className={cx("w-50 h-100 border-gray-400", {
          "border-start": hasChildren && isOpen,
        })}
      ></div>
    </div>
    <div className="h-100 flex-grow-1"></div>
  </div>
);

type CompactSpacerProps = {
  width: number;
  hasChildren: boolean;
  isOpen: boolean;
};

const CompactSpacer = ({ width, hasChildren, isOpen }: CompactSpacerProps) => (
  <div className={cx("w-100 d-flex flex-row", s.bottomSpacer)}>
    <div
      className={cx("w-100 h-100 border-gray-400", {
        "border-start": hasChildren && isOpen,
      })}
      style={{ width }}
    ></div>
  </div>
);
